import type { Question, QuestionBank, Part } from '../types';

// The question bank is external and hot-swappable (public/questions.json) —
// dropping in a new file (or a different exam's set) is all a new drill needs.

let cache: QuestionBank | null = null;

export async function loadBank(): Promise<QuestionBank> {
  if (cache) return cache;
  const res = await fetch('/questions.json', { cache: 'no-cache' });
  if (!res.ok) throw new Error(`Could not load question bank (${res.status})`);
  const bank = (await res.json()) as QuestionBank;
  if (!bank || !Array.isArray(bank.questions)) throw new Error('Question bank is malformed');
  cache = bank;
  return bank;
}

export function topicsForPart(bank: QuestionBank, part: Part): string[] {
  const seen = new Set<string>();
  for (const q of bank.questions) if (q.part === part) seen.add(q.topic);
  return [...seen];
}

/**
 * Build the question list for a drill: given part + selected topics, return the
 * questions in a lightly shuffled order (deterministic within a session start).
 * If no topics are selected, use all topics for the part.
 */
export function buildDrill(
  bank: QuestionBank,
  part: Part,
  topics: string[],
  limit: number,
  rng: () => number = Math.random,
): Question[] {
  const pool = bank.questions.filter(
    (q) => q.part === part && (topics.length === 0 || topics.includes(q.topic)),
  );
  // Shuffle (Fisher–Yates) then cap.
  const arr = [...pool];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr.slice(0, limit);
}
